import { useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import {
  LayoutDashboard, KanbanSquare, Users, Building2, UserRound, Sparkles,
  LogOut, Settings, ShieldCheck, ClipboardList, Inbox, Bell, Check,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { fmtDate } from "@/lib/meta";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/pipeline", label: "Pipeline", icon: KanbanSquare, testid: "nav-pipeline" },
  { to: "/bd-tracker", label: "BD Tracker", icon: ClipboardList, testid: "nav-bd" },
  { to: "/leads", label: "Leads", icon: Inbox, testid: "nav-leads", badge: "review" },
  { to: "/properties", label: "Properties", icon: Building2, testid: "nav-properties" },
  { to: "/consultants", label: "Consultants", icon: UserRound, testid: "nav-consultants" },
  { to: "/owners", label: "Owners", icon: Users, testid: "nav-owners" },
  { to: "/intelligence", label: "Intelligence", icon: Sparkles, testid: "nav-intelligence" },
  { to: "/team", label: "Team", icon: ShieldCheck, testid: "nav-team", adminOnly: true },
  { to: "/settings", label: "Settings", icon: Settings, testid: "nav-settings", adminOnly: true },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [reviewCount, setReviewCount] = useState(0);

  useEffect(() => {
    const load = () => api.get("/leads/review-count").then(({ data }) => setReviewCount(data.count)).catch(() => {});
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, []);

  const doLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-[#F9FAFB]">
      <aside className="w-60 shrink-0 bg-white border-r border-gray-200 flex flex-col fixed h-screen z-20">
        <div className="px-5 py-5 border-b border-gray-200 flex items-center gap-2.5">
          <div className="h-9 w-9 rounded-md bg-[#0047FF] flex items-center justify-center text-white font-heading font-extrabold">g</div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold text-[15px] tracking-tight text-gray-900">goStops</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-gray-400">Franchise CRM</div>
          </div>
          <div className="ml-auto"><NotificationBell /></div>
        </div>

        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV.filter((i) => !i.adminOnly || user?.role === "admin").map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                  isActive ? "bg-[#0047FF] text-white" : "text-gray-600 hover:bg-gray-100 hover:text-gray-900"
                }`
              }
            >
              <item.icon className="h-[18px] w-[18px]" />
              <span className="flex-1">{item.label}</span>
              {item.badge === "review" && reviewCount > 0 && (
                <span data-testid="review-badge" className="text-[10px] font-bold bg-amber-500 text-white rounded-full px-1.5 py-0.5 min-w-[18px] text-center">
                  {reviewCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="px-3 py-3 border-t border-gray-200">
          <div className="px-2 py-1.5 mb-1">
            <div className="text-sm font-semibold text-gray-900 truncate">{user?.name}</div>
            <div className="text-[11px] text-gray-400 truncate">{user?.email}</div>
            <span className="inline-block mt-1 text-[10px] uppercase tracking-wider bg-blue-50 text-[#0047FF] px-2 py-0.5 rounded">{user?.role}</span>
          </div>
          <Button variant="ghost" onClick={doLogout} data-testid="logout-button"
            className="w-full justify-start gap-3 text-gray-600 hover:bg-gray-100 hover:text-gray-900">
            <LogOut className="h-[18px] w-[18px]" /> Log out
          </Button>
        </div>
      </aside>

      <main className="flex-1 ml-60 min-w-0 fade-in">
        <Outlet />
      </main>
    </div>
  );
}

function NotificationBell() {
  const [data, setData] = useState({ items: [], unread: 0 });
  const load = () => api.get("/notifications").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); const t = setInterval(load, 30000); return () => clearInterval(t); }, []);
  const markAll = async () => { await api.post("/notifications/read-all"); load(); };
  const openItem = async (n) => { if (!n.read) { await api.post(`/notifications/${n.id}/read`); load(); } };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button className="relative p-1.5 rounded-md text-gray-500 hover:bg-gray-100 hover:text-gray-900 transition-colors" data-testid="notif-bell">
          <Bell className="h-[18px] w-[18px]" />
          {data.unread > 0 && (
            <span data-testid="notif-unread" className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-[#0047FF] text-white text-[9px] font-bold flex items-center justify-center">{data.unread}</span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="bg-white w-80 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200">
          <span className="font-heading font-bold text-sm">Notifications</span>
          {data.unread > 0 && (
            <button onClick={markAll} className="text-xs text-[#0047FF] flex items-center gap-1 hover:underline" data-testid="notif-mark-all"><Check className="h-3 w-3" />Mark all read</button>
          )}
        </div>
        <div className="max-h-80 overflow-y-auto">
          {data.items.length === 0 ? (
            <div className="text-sm text-gray-400 py-8 text-center">You're all caught up.</div>
          ) : data.items.map((n) => (
            <button key={n.id} onClick={() => openItem(n)} data-testid={`notif-item-${n.id}`}
              className={`w-full text-left px-3 py-2.5 border-b border-gray-100 hover:bg-gray-50 ${n.read ? "" : "bg-blue-50/60"}`}>
              <div className="text-sm text-gray-800">{n.message}</div>
              <div className="text-[11px] text-gray-400 mt-0.5">{fmtDate(n.created_at)}</div>
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function PageHeader({ title, subtitle, children }) {
  return (
    <div className="sticky top-0 z-10 bg-[#F9FAFB]/90 backdrop-blur border-b border-gray-200 px-8 py-5 flex items-center justify-between">
      <div>
        <h1 className="font-heading text-2xl font-extrabold tracking-tight text-gray-900">{title}</h1>
        {subtitle && <p className="text-sm text-gray-500 mt-0.5">{subtitle}</p>}
      </div>
      <div className="flex items-center gap-2">{children}</div>
    </div>
  );
}
